import axios from 'axios'
import { getAuthHeaders, handleApiError, getErrorMessage, ApiResponse } from './apiConfig'

// Utiliser l'instance axios globale configurée dans main.ts
const http = axios

interface ListMembersParams {
  pageNumber?: number
  sortDir?: string
  sortColumn?: string
  sortOption?: string
  borneInf?: string
  borneSup?: string
  pays_id?: string
  collectivite_id?: string
  search?: string
  trashed?: number
}

interface Member {
  id: number
  nom: string
  prenom: string
  telephone: string
  email: string
  photo?: string
  collectivite?: {
    id: number
    nom: string
  }
  pays?: {
    id: number
    nom: string
  }
  created_at: string
  actif: number
  deleted_at?: string | null
  fonction?: string
}

interface ListMembersResponse {
  data: Member[]
  total: number
  per_page: number
  last_page: number
}

class MembersService {
  async getStartupInfos() {
    try {
      const response = await http.get('/backoffice/users/get_startupinfos_list_datas', {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async listMembers(params: ListMembersParams = {}): Promise<ListMembersResponse> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      // Utiliser la route frontoffice dédiée si collectivite_id est présent, sinon backoffice
      const hasCollectiviteId = params.collectivite_id && params.collectivite_id !== ''
      const endpoint = hasCollectiviteId 
        ? `/collectivites/membres/list?${queryString}`
        : `/backoffice/users/list?${queryString}`
      
      const response = await http.get(endpoint, {
        headers: getAuthHeaders()
      })
      
      // LengthAwarePaginator retourne directement les propriétés
      // La réponse devrait avoir: data, total, per_page, last_page, current_page, etc.
      return response.data
    } catch (error: any) {
      console.error('Erreur listMembers:', error)
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMember(id: number): Promise<Member> {
    try {
      const response = await http.get(`/backoffice/users/get/${id}`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async deleteMember(id: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/backoffice/users/destroy/${id}`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async restoreMember(id: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/backoffice/users/restore/${id}`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async toggleEtatValide(ids: number[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/toggle_etat_valide', { ids }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async exportToExcel(params: ListMembersParams = {}): Promise<Blob> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      const response = await http.get(`/backoffice/users/export_excel?${queryString}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async exportToPdf(params: ListMembersParams = {}): Promise<Blob> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      const response = await http.get(`/backoffice/users/export_pdf?${queryString}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async updateMember(formData: FormData): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/save', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async sendConfirmationEmail(userIds: number[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/send_confirmation_email', { users_ids: userIds }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  // Méthodes spécifiques pour le frontoffice (gérant de collectivité)
  async downloadCsvTemplate(): Promise<Blob> {
    try {
      const response = await http.get('/collectivites/membres/download_csv_template', {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async prepareImportFile(file: File): Promise<any[]> {
    try {
      const formData = new FormData()
      formData.append('import_file', file)
      
      const response = await http.post('/collectivites/membres/prepare_import_file', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async importFileDatas(importDatas: any[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/collectivites/membres/import_file_datas', {
        import_datas: importDatas
      }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async countUnverifiedMembers(): Promise<{ count: number }> {
    try {
      const response = await http.get('/collectivites/membres/count_unverified', {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async sendInvitationsUnverified(): Promise<ApiResponse> {
    try {
      const response = await http.post('/collectivites/membres/send_invitations_unverified', {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberFormDatas(id: number): Promise<{ user: Member, collectivite_id: number }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/form_datas`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  // Méthode spécifique pour le frontoffice (gérant de collectivité)
  async updateMemberFrontoffice(id: number, formData: FormData): Promise<ApiResponse> {
    try {
      const response = await http.post(`/collectivites/membres/${id}/update`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }
}

export default new MembersService()

